import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import memeCoinIcon from '../assets/meme-coin.png';
import { fmtUsd } from '../utils/fmtPrice';
import './Trading.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const Trading = () => {
  const navigate = useNavigate();
  const [omniPortfolio, setOmniPortfolio] = useState(null);
  const [discAccess, setDiscAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('sessionToken');
    if (!token) return undefined;
    axios
      .get(`${API_BASE}/disc-scalping/access`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!cancelled) setDiscAccess(!!data?.allowed);
      })
      .catch(() => {
        if (!cancelled) setDiscAccess(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios
        .get(`${API_BASE}/multi-asset/omni-portfolio`)
        .then(({ data }) => {
          if (!cancelled) setOmniPortfolio(data);
        })
        .catch(() => {
          if (!cancelled) setOmniPortfolio(null);
        });
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  const omniStats =
    omniPortfolio?.ytdPct != null
      ? {
          equity: fmtUsd(omniPortfolio.totalEquityUsd),
          ytd: omniPortfolio.ytdLabel || `${omniPortfolio.ytdPct >= 0 ? '+' : ''}${omniPortfolio.ytdPct}% YTD`,
        }
      : null;

  const methodologies = [
    {
      id: 'stocks',
      title: 'Single Asset',
      description: 'Hyperliquid single-market trading with per-asset max leverage',
      icon: '📊',
      status: 'active',
      stats: null
    },
    {
      id: 'multi-asset',
      title: 'Multi-Asset (MAP)',
      description: 'Trade across multiple asset classes — manual or oBot',
      icon: '🌐',
      status: 'active',
      stats: omniStats
        ? { activeTrades: omniStats.equity, winRate: omniStats.ytd }
        : null,
      statLabels: omniStats ? { activeTrades: 'Omni book', winRate: 'Plan YTD' } : null,
    },
    {
      id: 'meme-coins',
      title: 'Meme Coins',
      description: 'High-volatility trading on trending meme tokens',
      icon: memeCoinIcon,
      status: 'active',
      stats: { activeTrades: 12, winRate: '68%' }
    },
    {
      id: 'scalping',
      title: 'Scalping',
      description: 'Oracle-Anchored Adaptive Scalper — automated oBot, 24/7 (paper validation)',
      icon: '⚡',
      status: 'active',
      stats: null
    },
    ...(discAccess
      ? [
          {
            id: 'disc-scalping',
            title: 'Discretionary Scalping',
            description: 'Manual scalping desk on Hyperliquid — live order book, one-click orders, paper or live',
            icon: '🎯',
            status: 'active',
            stats: null
          },
        ]
      : []),
    {
      id: 'ovebot',
      title: 'oveBot',
      description: 'Omni Volatility Expansion — HL perp breakouts across crypto, indices, stocks & commodities',
      icon: '⚡',
      status: 'active',
      stats: null
    },
    {
      id: 'funding-arbitrage',
      title: 'Funding Arbitrage',
      description: 'Delta-neutral HL carry — long spot + short perp, paper validation (Phase 0)',
      icon: '⚖️',
      status: 'active',
      stats: null
    },
    {
      id: 'correlation',
      title: 'Correlation',
      description: 'Trading based on asset correlation patterns',
      icon: '🔗',
      status: 'active',
      stats: { activeTrades: 8, winRate: '75%' }
    },
    {
      id: 'turtle',
      title: 'Turtle',
      description: 'Trend-following strategy based on breakout levels',
      icon: '🐢',
      status: 'active',
      stats: { activeTrades: 5, winRate: '81%' }
    },
    {
      id: 'dayswing',
      title: 'Day/Swing',
      description: 'Cross-exchange price difference exploitation',
      icon: '⚖️',
      status: 'coming-soon',
      stats: null
    }
  ];

  const handleCardClick = (methodology) => {
    if (methodology.status === 'coming-soon') {
      alert(`${methodology.title} coming soon!`);
    } else {
      // Navigate to specific trading screen
      if (methodology.id === 'multi-asset') {
        navigate('/multi-asset');
      } else if (methodology.id === 'meme-coins') {
        navigate('/memecoins');
      } else if (methodology.id === 'stocks') {
        navigate('/single-asset-hl');
      } else if (methodology.id === 'scalping') {
        navigate('/scalping');
      } else if (methodology.id === 'disc-scalping') {
        navigate('/discretionary-scalping');
      } else if (methodology.id === 'ovebot' || methodology.id === 'funding-carry') {
        navigate('/ovebot');
      } else if (methodology.id === 'funding-arbitrage') {
        navigate('/funding-arbitrage');
      } else {
        alert(`${methodology.title} page coming soon!`);
      }
    }
  };

  return (
    <div className="trading-page">
      <div className="trading-header">
        <h1>Trading Dashboard</h1>
        <p className="subtitle">Select your trading methodology to get started</p>
      </div>

      <div className="methodology-grid">
        {methodologies.map((method) => (
          <div
            key={method.id}
            className={`methodology-card ${method.status}`}
            onClick={() => handleCardClick(method)}
          >
            <div className="card-icon">
  {typeof method.icon === 'string' && (method.icon.startsWith('/') || method.icon.includes('.png') || method.icon.includes('.jpg')) ? (
    <img src={method.icon} alt={method.title} className="icon-image" />
  ) : typeof method.icon === 'string' ? (
    <span className="icon-emoji">{method.icon}</span>
  ) : (
    <img src={method.icon} alt={method.title} className="icon-image" />
  )}
</div>
            <div className="card-content">
              <h3>{method.title}</h3>
              <p className="card-description">{method.description}</p>
              
              {method.stats && (
                <div className="card-stats">
                  <div className="stat">
                    <span className="stat-label">{method.statLabels?.activeTrades || 'Active Trades'}</span>
                    <span className="stat-value">{method.stats.activeTrades}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">{method.statLabels?.winRate || 'Win Rate'}</span>
                    <span
                      className={`stat-value${
                        method.id === 'multi-asset' && omniPortfolio?.ytdPct != null
                          ? omniPortfolio.ytdPct >= 0
                            ? ' stat-positive'
                            : ' stat-negative'
                          : ''
                      }`}
                    >
                      {method.stats.winRate}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="card-footer">
                {method.status === 'active' && (
                  <span className="status-badge active">Active</span>
                )}
                {method.status === 'coming-soon' && (
                  <span className="status-badge coming-soon">Coming Soon</span>
                )}
                <span className="card-arrow">→</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Trading;
